import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import i18n from '@/i18n';
import { getEmailBalance } from '@/features/CampaignsEmail/services/getEmailBalance';
import { formatDecimal } from '@/utilities/formatters/decimal';
import { formatDate } from '@/utilities/formatters/dates';
export function useEmailBalance() {
    const store = useStore();
    const loading = ref(false);
    const error = ref(null);
    const emailBalance = ref('0');
    const lastBalanceUpdate = ref('');
    const emailBalanceResetDate = ref('');
    const brandId = computed(() => {
        const selectedBrand = store.getters.selectedBrand;
        if (selectedBrand) {
            if (typeof selectedBrand === 'object' && 'id' in selectedBrand) {
                return selectedBrand.id;
            }
            return selectedBrand;
        }
        const user = store.getters.user;
        return (user?.brand_id ?? null);
    });
    const lastFetchedBrand = ref(null);
    async function fetchEmailBalance() {
        const b = brandId.value ?? null;
        if (b && lastFetchedBrand.value === b)
            return;
        lastFetchedBrand.value = b;
        loading.value = true;
        error.value = null;
        try {
            if (!b)
                throw new Error(i18n.global.t('breadcrumb.email.campaigns.errors.brand-not-found'));
            const data = await getEmailBalance();
            emailBalance.value = data.remaining !== undefined ? formatDecimal(data.remaining, 0) : '0';
            if (data.last_synced_at) {
                lastBalanceUpdate.value = formatDate(data.last_synced_at, 'DD/MM/YYYY [às] HH:mm:ss');
            }
            if (data.period) {
                const [year, month] = data.period.split('-').map(Number);
                const lastDay = new Date(year, month, 0);
                emailBalanceResetDate.value = formatDate(lastDay.toISOString(), 'DD/MM/YY');
            }
        }
        catch (e) {
            const message = e instanceof Error ? e.message : i18n.global.t('breadcrumb.email.campaigns.errors.fetch-email-balance-failed');
            error.value = message;
            emailBalance.value = '0';
            lastBalanceUpdate.value = '';
            emailBalanceResetDate.value = '';
        }
        finally {
            loading.value = false;
        }
    }
    watch(() => brandId.value, () => void fetchEmailBalance(), { immediate: true });
    onMounted(() => {
        void fetchEmailBalance();
    });
    return {
        loading,
        error,
        emailBalance,
        lastBalanceUpdate,
        emailBalanceResetDate,
        fetchEmailBalance,
    };
}
